import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { toast } from '../../ui/toast';
import {
  agenticAssessmentApi,
  agenticErrorMessage,
  useAnswerAgenticQuestionMutation,
  useAttachAgenticVideoMutation,
  useEndAgenticSessionMutation,
  useGetAgenticAvatarMutation,
  useGetAgenticCandidateSessionQuery,
  useGetAgenticRecordingUrlsMutation,
  useRetryAgenticTurnMutation,
  useStartAgenticSessionMutation,
} from '../../utils/api_service/agenticAssessmentApi';
import { store } from '../../utils/redux_store/store';
import { AgenticCandidateSession, AgenticCandidateTurn, AgenticRecordingUrls } from '../../utils/types/agentic_assessment_types';
import { MAX_RECORDING_SECONDS, useAnswerRecorder } from './useAnswerRecorder';
import { useInterviewerAvatar } from './useInterviewerAvatar';

const STALE_SECONDS = 5 * 60;

const isWorking = (turn?: AgenticCandidateTurn) => !!turn && (turn.status === 'Generating' || turn.status === 'Answered');
const isStale = (turn?: AgenticCandidateTurn) =>
  isWorking(turn) && !!turn!.requestedAt && Date.now() / 1000 - turn!.requestedAt > STALE_SECONDS;
const clock = (seconds: number) => `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;

const putFile = async (url: string, file: Blob, contentType: string) => {
  const res = await fetch(url, { method: 'PUT', body: file, headers: { 'Content-Type': contentType } });
  if (!res.ok) throw new Error(`upload failed (${res.status})`);
};

const Tips: React.FC = () => (
  <div className='rounded-2xl border border-gray-100 bg-white p-4 shadow-sm'>
    <h4 className='mb-3 text-xs font-bold uppercase tracking-widest text-gray-900'>Tips</h4>
    <ul className='space-y-2 text-[13px] text-gray-600'>
      <li className='flex gap-2'><span className='mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-green-400' />Look at the camera and speak clearly.</li>
      <li className='flex gap-2'><span className='mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-green-400' />Use real examples and explain your reasoning.</li>
      <li className='flex gap-2'><span className='mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-green-400' />Each answer can be up to { MAX_RECORDING_SECONDS / 60 } minutes. You can redo it before you submit.</li>
      <li className='flex gap-2'><span className='mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-green-400' />It&apos;s fine to say you don&apos;t know.</li>
    </ul>
  </div>
);

export const AgenticInterview: React.FC = () => {
  const { sessionId } = useParams<{ sessionId: string }>();
  const [poll, setPoll] = useState(false);
  const { data: session, isLoading, error } = useGetAgenticCandidateSessionQuery(sessionId, {
    pollingInterval: poll ? 1000 : 0,
    refetchOnMountOrArgChange: true,
  });
  const [start, { isLoading: starting }] = useStartAgenticSessionMutation();
  const [getUrls] = useGetAgenticRecordingUrlsMutation();
  const [answer] = useAnswerAgenticQuestionMutation();
  const [attachVideo] = useAttachAgenticVideoMutation();
  // Upload URLs for the open question, fetched while the candidate is still recording.
  const prefetchedUrls = useRef<{ number: number; urls: Promise<AgenticRecordingUrls> } | null>(null);
  const showNow = (fresh: AgenticCandidateSession) => {
    store.dispatch(agenticAssessmentApi.util.updateQueryData('getAgenticCandidateSession', sessionId, () => fresh));
  };
  // Videos still uploading in the background, after their answer was already submitted on audio.
  const pendingVideos = useRef(0);
  const [videosUploading, setVideosUploading] = useState(0);
  const [retry, { isLoading: retrying }] = useRetryAgenticTurnMutation();
  const [endSession, { isLoading: ending }] = useEndAgenticSessionMutation();
  const [confirmingEnd, setConfirmingEnd] = useState(false);
  const recorder = useAnswerRecorder();
  const [submitting, setSubmitting] = useState(false);

  const turns = session?.turns ?? [];
  const last = turns[turns.length - 1];
  const asked = last && last.status === 'Asked' ? last : undefined;
  const answeredCount = useMemo(() => turns.filter((t) => t.status === 'Evaluated').length, [turns]);
  const inProgress = session?.status === 'In Progress';
  const endedEarly = inProgress && !!session?.endedEarly;

  const [getAvatar] = useGetAgenticAvatarMutation();
  const avatar = useInterviewerAvatar(inProgress && !endedEarly, async () => {
    const connection = await getAvatar(sessionId).unwrap();
    return connection.enabled ? connection.token : null;
  });
  // Each question is read aloud once when it appears; the candidate can replay it.
  const spokenNumber = useRef<number | null>(null);
  useEffect(() => {
    if (avatar.status === 'ready' && asked?.question && spokenNumber.current !== asked.number) {
      spokenNumber.current = asked.number;
      avatar.speak(asked.question);
    }
  }, [avatar.status, asked?.number]); // eslint-disable-line

  useEffect(() => {
    setPoll(!!session && session.status === 'In Progress' && (isWorking(last) || !!session.endedEarly));
  }, [session, last]);

  // Leaving while a video is still uploading would lose it.
  useEffect(() => {
    if (!videosUploading) return undefined;
    const warn = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [videosUploading]);

  // A new question is a clean slate for the recorder, and its upload URLs can be fetched now.
  useEffect(() => {
    if (asked) {
      const urls = getUrls({ sessionId, number: asked.number }).unwrap();
      urls.catch(() => undefined);
      prefetchedUrls.current = { number: asked.number, urls };
    }
    recorder.reset();
  }, [asked?.number]); // eslint-disable-line

  // Turn the camera on as soon as the candidate is in the interview (or on the intro, for a check).
  useEffect(() => {
    if (session && (session.status === 'In Progress' || session.status === 'Invite Sent')) recorder.startCamera();
  }, [session?.status]); // eslint-disable-line

  if (isLoading) {
    return <div className='py-24 text-center text-sm text-gray-500'>Loading your interview…</div>;
  }
  if (error || !session) {
    return (
      <div className='mx-auto max-w-lg py-24 text-center'>
        <h1 className='text-xl font-semibold text-gray-900'>We couldn&apos;t find this interview</h1>
        <p className='mt-2 text-sm text-gray-600'>
          The link may be for a different account, or the interview was replaced by a newer invite.
          Check your email for the latest link.
        </p>
        <Link to='/assigned-assessments' className='mt-6 inline-block text-sm font-semibold text-blue-700'>Back to my assessments</Link>
      </div>
    );
  }

  const handleStart = async () => {
    try {
      // The response already holds question 1 when it was prepared: show it without waiting to poll.
      showNow(await start(sessionId).unwrap());
      setPoll(true);
    } catch (e) {
      toast.error(agenticErrorMessage(e, 'Could not start the interview.'));
    }
  };

  const handleSubmit = async () => {
    if (!asked || !recorder.recording) return;
    setSubmitting(true);
    try {
      const { number } = asked;
      const { video } = recorder.recording;
      const urls = prefetchedUrls.current?.number === number
        ? await prefetchedUrls.current.urls.catch(() => getUrls({ sessionId, number }).unwrap())
        : await getUrls({ sessionId, number }).unwrap();
      // Submit as soon as the small audio track is up, so scoring starts; the video follows.
      await putFile(urls.audio.url, recorder.recording.audio, 'audio/webm');
      showNow(await answer({ sessionId, number, audioKey: urls.audio.key }).unwrap());
      setPoll(true);
      uploadVideoInBackground(number, urls.video, video);
    } catch (e) {
      toast.error(agenticErrorMessage(e, 'Your answer was not sent. Check your connection and submit again.'));
    } finally {
      setSubmitting(false);
    }
  };

  const uploadVideoInBackground = (number: number, target: { key: string; url: string }, video: Blob) => {
    pendingVideos.current += 1;
    setVideosUploading(pendingVideos.current);
    const attempt = async (tries: number): Promise<void> => {
      try {
        await putFile(target.url, video, 'video/webm');
        await attachVideo({ sessionId, number, videoKey: target.key }).unwrap();
      } catch (e) {
        if (tries > 1) return attempt(tries - 1);
        toast.error('The video of one answer could not be uploaded. Your answer was still recorded.');
      }
      return undefined;
    };
    attempt(3).finally(() => {
      pendingVideos.current -= 1;
      setVideosUploading(pendingVideos.current);
    });
  };

  const handleRetry = async () => {
    if (!last) return;
    try {
      await retry({ sessionId, number: last.number }).unwrap();
      setPoll(true);
    } catch (e) {
      toast.error(agenticErrorMessage(e, 'Could not retry yet.'));
    }
  };

  const handleEnd = async () => {
    try {
      showNow(await endSession(sessionId).unwrap());
      setConfirmingEnd(false);
      setPoll(true);
    } catch (e) {
      setConfirmingEnd(false);
      toast.error(agenticErrorMessage(e, 'Could not end the interview. Try again in a moment.'));
    }
  };

  const header = (
    <div className='rounded-2xl bg-white p-3 shadow-sm'>
      <div className='flex flex-wrap items-center gap-3'>
        { session.companyLogo && <img src={session.companyLogo} alt='' className='h-8 w-auto' /> }
        <div className='min-w-0'>
          <div className='truncate text-sm font-semibold text-gray-900'>{ session.jobTitle }</div>
          <div className='truncate text-xs text-gray-500'>{ session.organizationName } · video interview</div>
        </div>
        { inProgress && last && (
          <div className='ml-auto flex flex-wrap gap-2'>
            <span className='rounded-full bg-blue-100 px-4 py-2 text-sm font-medium text-blue-700'>Question { last.number }</span>
            <span className='rounded-full bg-green-100 px-4 py-2 text-sm font-medium text-green-700'>Video Response</span>
          </div>
        ) }
      </div>
      { inProgress && session.maxQuestions && (
        <div className='mt-3'>
          <div className='h-1.5 w-full overflow-hidden rounded-full bg-gray-200'>
            <div className='h-full rounded-full bg-blue-600 transition-all duration-500 ease-out'
                style={{ width: `${Math.min(100, (answeredCount / session.maxQuestions) * 100)}%` }} />
          </div>
          <p className='mt-1 text-[11px] font-medium uppercase tracking-widest text-gray-500'>
            { answeredCount } answered · up to { session.maxQuestions } questions
          </p>
        </div>
      ) }
    </div>
  );

  const camera = (
    <div className='relative overflow-hidden rounded-2xl border border-gray-200 bg-gray-950 p-1 shadow-sm'>
      { recorder.state === 'recorded' && recorder.recording ? (
        <video key={recorder.recording.url} src={recorder.recording.url} controls className='aspect-video w-full rounded-xl bg-black object-contain' />
      ) : (
        <video ref={recorder.previewRef} muted playsInline className='aspect-video w-full -scale-x-100 rounded-xl bg-black object-cover' />
      ) }
      { recorder.state === 'recording' && (
        <div className='absolute left-3 top-3 flex items-center gap-2 rounded-full bg-black/60 px-3 py-1 text-xs font-semibold text-white'>
          <span className='h-2.5 w-2.5 animate-pulse rounded-full bg-red-500' />
          REC { clock(recorder.elapsed) } / { clock(MAX_RECORDING_SECONDS) }
        </div>
      ) }
      { recorder.cameraError && (
        <div className='absolute inset-0 flex items-center justify-center bg-gray-950/90 p-6 text-center text-sm text-white'>
          { recorder.cameraError }
        </div>
      ) }
    </div>
  );

  if (session.status === 'Invite Sent') {
    return (
      <div className='mx-auto w-full max-w-7xl bg-gray-50 p-2 md:p-4'>
        <div className='grid grid-cols-1 gap-4 md:grid-cols-3'>
          <div className='flex flex-col gap-4 md:col-span-2'>
            { header }
            <div className='rounded-2xl bg-white p-6 shadow-sm'>
              <h1 className='text-2xl font-bold text-gray-900'>Before you begin</h1>
              <ul className='mt-3 flex flex-col gap-2 text-[15px] text-gray-700'>
                <li>You&apos;ll answer one question at a time on video. Each question follows on from your previous answers.</li>
                <li>Most people finish in 20 to 30 minutes. There is no overall time limit, and you can leave and come back.</li>
                <li>Check that you can see yourself below, then start.</li>
              </ul>
            </div>
            { camera }
            <button
                type='button'
                disabled={starting || !recorder.cameraReady}
                onClick={handleStart}
                className='self-start rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 px-6 py-3 text-sm font-bold text-white shadow-lg shadow-blue-200 transition hover:-translate-y-0.5 disabled:cursor-not-allowed disabled:opacity-50'
            >
              { starting ? 'Starting…' : 'Start the interview' }
            </button>
          </div>
          <div className='hidden md:block'><Tips /></div>
        </div>
      </div>
    );
  }

  if (session.status === 'Expired') {
    return (
      <div className='mx-auto max-w-lg py-24 text-center'>
        <h1 className='text-xl font-semibold text-gray-900'>This interview link has expired</h1>
        <p className='mt-2 text-sm text-gray-600'>Contact the recruiter if you&apos;d like a new invite.</p>
      </div>
    );
  }

  if (session.status === 'Completed') {
    return (
      <div className='mx-auto w-full max-w-3xl p-4'>
        <div className='rounded-2xl border border-green-200 bg-green-50 px-6 py-10 text-center'>
          <div className='text-2xl font-bold text-green-900'>
            { session.endedEarly ? 'Your interview has ended. Thank you.' : 'That\'s everything. Thank you.' }
          </div>
          <p className='mt-2 text-sm text-green-800'>The hiring team will review your answers and be in touch.</p>
          { videosUploading > 0 && (
            <p className='mt-3 animate-pulse text-sm font-semibold text-green-900'>
              Finishing the upload of your last answer. Please keep this page open.
            </p>
          ) }
          <Link to='/assigned-assessments' className='mt-6 inline-block text-sm font-semibold text-green-800 underline'>Back to my assessments</Link>
        </div>
      </div>
    );
  }

  if (endedEarly) {
    return (
      <div className='mx-auto max-w-lg py-24 text-center'>
        <h1 className='animate-pulse text-xl font-semibold text-gray-900'>Ending your interview…</h1>
        <p className='mt-2 text-sm text-gray-600'>Your answers so far are being saved. This only takes a moment.</p>
      </div>
    );
  }

  const stale = isStale(last);
  const failed = last?.status === 'Failed' || stale;
  const waiting = (isWorking(last) && !stale) || submitting;

  const open = !!asked && !waiting && !failed;
  const withAvatar = avatar.status !== 'off';
  const reviewing = recorder.state === 'recorded' && !!recorder.recording;
  // With Holly on stage the candidate is a corner tile (grown while reviewing a take); without her, the stage.
  const cameraTile = !withAvatar
    ? 'absolute inset-0'
    : reviewing
      ? 'absolute inset-3 z-20 rounded-xl border-4 border-white shadow-lg md:inset-4'
      : 'absolute bottom-3 right-3 z-20 h-32 w-24 rounded-xl border-[3px] border-white shadow-lg md:bottom-4 md:right-4 md:h-[150px] md:w-60';
  const status = avatar.speaking
    ? 'Holly is asking. You can record when she finishes.'
    : recorder.state === 'recording'
      ? 'Recording. Press stop when you have finished your answer.'
      : reviewing
        ? 'Play back your answer, then submit it or record it again.'
        : 'Record your answer when you are ready.';

  return (
    <div className='mx-auto flex w-full max-w-7xl flex-col gap-4 bg-gray-50 p-2 md:p-4'>
      <div className='flex flex-wrap items-center gap-3 rounded-2xl bg-white p-3 shadow-sm'>
        { session.companyLogo && <img src={session.companyLogo} alt='' className='h-8 w-auto' /> }
        <div className='min-w-0'>
          <div className='truncate text-sm font-semibold text-gray-900'>{ session.jobTitle }</div>
          <div className='truncate text-xs text-gray-500'>{ session.organizationName } · video interview</div>
        </div>
        <div className='ml-auto flex items-center gap-4'>
          { session.maxQuestions && (
            <div className='hidden w-52 sm:block'>
              <p className='mb-1 text-[11px] font-semibold uppercase tracking-widest text-gray-500'>
                { answeredCount } answered · up to { session.maxQuestions }
              </p>
              <div className='h-1.5 w-full overflow-hidden rounded-full bg-gray-200'>
                <div className='h-full rounded-full bg-primary-gradient transition-all duration-500 ease-out'
                    style={{ width: `${Math.max(4, Math.min(100, (answeredCount / session.maxQuestions) * 100))}%` }} />
              </div>
            </div>
          ) }
          <button
              type='button'
              onClick={() => setConfirmingEnd(true)}
              disabled={last?.status === 'Answered' || submitting || ending}
              title={last?.status === 'Answered' ? 'Wait until your last answer has been processed' : undefined}
              className='rounded-xl border border-red-200 bg-white px-4 py-2 text-sm font-semibold text-red-700 transition hover:bg-red-50 disabled:cursor-not-allowed disabled:border-gray-200 disabled:text-gray-400'
          >
            End interview
          </button>
        </div>
      </div>

      <div className='flex flex-col gap-4 md:flex-row'>
        <div className='relative aspect-[4/3] overflow-hidden rounded-2xl bg-gray-200 shadow-sm md:aspect-auto md:min-h-[560px] md:flex-1'>
          <div ref={avatar.containerRef} className={withAvatar ? 'interviewer-avatar absolute inset-0' : 'hidden'} />
          { withAvatar && (
            <div className='absolute left-3 top-3 z-20 flex items-center gap-2 rounded-full bg-white/90 px-3 py-1.5 text-xs font-semibold text-gray-900 shadow-sm md:left-4 md:top-4 md:text-sm'>
              <svg width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round'
                  aria-hidden='true' className={avatar.speaking ? 'animate-pulse text-primary' : 'text-gray-400'}>
                <path d='M3 10v4M7 7v10M11 4v16M15 8v8M19 11v2' />
              </svg>
              { avatar.status === 'connecting' ? 'Connecting to Holly…' : `Holly · AI interviewer${avatar.speaking ? ' · speaking' : ''}` }
            </div>
          ) }

          <div className={`${cameraTile} overflow-hidden bg-gray-900`}>
            { reviewing ? (
              <video key={recorder.recording!.url} src={recorder.recording!.url} controls className='h-full w-full bg-black object-contain' />
            ) : (
              <video ref={recorder.previewRef} muted playsInline className='h-full w-full -scale-x-100 object-cover' />
            ) }
            { recorder.state === 'recording' && (
              <div className='absolute left-2 top-2 flex items-center gap-1.5 rounded-full bg-black/60 px-2 py-0.5 text-[11px] font-semibold text-white md:text-xs'>
                <span className='h-2 w-2 animate-pulse rounded-full bg-red-500' />
                { clock(recorder.elapsed) } / { clock(MAX_RECORDING_SECONDS) }
              </div>
            ) }
            { withAvatar && !reviewing && (
              <span className='absolute bottom-1.5 left-1.5 rounded-full bg-white/90 px-2 py-0.5 text-[11px] font-semibold text-gray-900'>You</span>
            ) }
            { recorder.cameraError && (
              <div className='absolute inset-0 flex items-center justify-center bg-gray-950/90 p-3 text-center text-xs text-white md:text-sm'>
                { recorder.cameraError }
              </div>
            ) }
          </div>

          { avatar.speaking && <div className='pointer-events-none absolute inset-0 z-30 rounded-2xl ring-[3px] ring-inset ring-emerald-500' /> }
        </div>

        <aside aria-label='Question and controls' className='flex flex-col gap-4 rounded-2xl bg-white p-5 shadow-sm md:w-[400px] md:shrink-0'>
          { last && (
            <div className='flex flex-wrap items-center gap-2'>
              <span className='rounded-full bg-emerald-50 px-3.5 py-1.5 text-sm font-semibold text-emerald-700'>Question { last.number }</span>
              <span className='rounded-full bg-sky-50 px-3.5 py-1.5 text-sm font-semibold text-sky-700'>Video response</span>
            </div>
          ) }

          { waiting ? (
            <p className='animate-pulse text-base font-medium text-gray-500 md:text-lg'>
              { submitting
                ? 'Uploading your answer…'
                : last?.status === 'Generating' && last.number === 1
                  ? 'Preparing your first question…'
                  : 'Please wait while the system processes your previous answer…' }
            </p>
          ) : failed ? (
            <div className='flex flex-wrap items-center gap-3 text-sm text-amber-800'>
              { last?.status === 'Answered' || (last?.status === 'Failed' && !!last.question)
                ? 'Your answer was saved, but we hit a problem processing it.'
                : 'We hit a problem preparing the next question.' }
              <button type='button' disabled={retrying} onClick={handleRetry} className='font-semibold text-amber-900 underline disabled:opacity-50'>
                Try again
              </button>
            </div>
          ) : (
            <p className='text-base font-medium leading-relaxed text-gray-900 md:text-lg'>{ asked?.question }</p>
          ) }

          { open && (
            <>
              <p className='flex items-center gap-2 text-sm text-gray-600'>
                <span className={`h-2 w-2 shrink-0 rounded-full ${recorder.state === 'recording' ? 'animate-pulse bg-red-500' : 'bg-primary'}`} />
                { status }
              </p>
              <div className='mt-auto flex flex-col gap-2.5'>
                { reviewing ? (
                  <button type='button' onClick={recorder.start} disabled={submitting}
                      className='h-[52px] rounded-xl border-2 border-gray-100 bg-white text-[15px] font-bold text-gray-700 transition hover:bg-gray-50 disabled:opacity-50'>
                    Record again
                  </button>
                ) : (
                  <button
                      type='button'
                      onClick={recorder.state === 'recording' ? recorder.stop : recorder.start}
                      disabled={!recorder.cameraReady || avatar.speaking || (recorder.state === 'recording' && !recorder.canStop)}
                      className={`flex h-[52px] items-center justify-center gap-2.5 rounded-xl text-[15px] font-bold text-white shadow-sm transition disabled:cursor-not-allowed disabled:opacity-50 ${
                        recorder.state === 'recording' ? 'bg-red-600 hover:bg-red-700' : 'bg-primary hover:bg-emerald-700'
                      }`}
                  >
                    { recorder.state === 'recording'
                      ? <span className='h-3 w-3 rounded-sm bg-white' />
                      : <span className='h-3 w-3 rounded-full bg-red-500 ring-2 ring-white' /> }
                    { recorder.state === 'recording' ? 'Stop recording' : 'Start recording' }
                  </button>
                ) }
                <div className={`grid gap-2.5 ${avatar.status === 'ready' ? 'grid-cols-2' : 'grid-cols-1'}`}>
                  { avatar.status === 'ready' && (
                    <button type='button' onClick={() => avatar.speak(asked?.question ?? '')}
                        disabled={avatar.speaking || recorder.state === 'recording' || submitting}
                        className='h-[46px] rounded-xl border-2 border-gray-100 bg-white text-sm font-semibold text-gray-700 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:text-gray-400'>
                      { avatar.speaking ? 'Asking…' : 'Replay question' }
                    </button>
                  ) }
                  <button
                      type='button'
                      onClick={handleSubmit}
                      disabled={!reviewing || submitting}
                      className='h-[46px] rounded-xl bg-primary text-sm font-semibold text-white transition hover:bg-emerald-700 disabled:cursor-not-allowed disabled:bg-gray-200 disabled:text-gray-500'
                  >
                    { submitting ? 'Submitting…' : 'Submit response' }
                  </button>
                </div>
                <p className='text-xs leading-relaxed text-gray-600'>
                  Answers can be up to { MAX_RECORDING_SECONDS / 60 } minutes. You can record again before you submit.
                </p>
              </div>
            </>
          ) }
        </aside>
      </div>

      { confirmingEnd && (
        <div className='fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4' role='dialog' aria-modal='true'>
          <div className='w-full max-w-md rounded-2xl bg-white p-6 shadow-xl'>
            <h2 className='text-lg font-bold text-gray-900'>End the interview now?</h2>
            <ul className='mt-3 flex flex-col gap-2 text-sm text-gray-700'>
              <li>You will <strong>not</strong> be able to retake or continue this assessment.</li>
              <li>The answers you have submitted will be scored. Questions you have not answered will score 0.</li>
              { recorder.state !== 'idle' && <li>The recording you have not submitted will be discarded.</li> }
            </ul>
            <div className='mt-6 flex justify-end gap-3'>
              <button type='button' onClick={() => setConfirmingEnd(false)} disabled={ending}
                  className='rounded-xl border border-gray-200 px-5 py-2.5 text-sm font-semibold text-gray-700 hover:bg-gray-50'>
                Keep going
              </button>
              <button type='button' onClick={handleEnd} disabled={ending}
                  className='rounded-xl bg-rose-600 px-5 py-2.5 text-sm font-bold text-white shadow-sm hover:bg-rose-700 disabled:opacity-50'>
                { ending ? 'Ending…' : 'End interview' }
              </button>
            </div>
          </div>
        </div>
      ) }
    </div>
  );
};
